import { useState } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';

import { ACCOUNT_PAGE_URL, DASHBOARD_URL, ACCOUNT_DELETED_URL } from '../constants/AppUrlConstants';
import { oneloginUrl, oneloginPostLogouturl } from '../constants/config';
import Auth from '../utils/Auth';
import handleAuthErrors from '../utils/API/handleAuthErrors';
import { navigateTo } from '../utils/navigation';

const ActionEntity = ({ notification }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { entityId } = useParams();
  const [isFormDisabled, setFormDisabled] = useState(false);
  const [error, setError] = useState(null);
  const [deletionConfirmed, setDeletionConfirmed] = useState(false);
  const { title, heading, entity, baseURL, redirectURL, apiHookConfig = [], action } = notification;
  const entityURL = entityId ? `${baseURL}/${entityId}` : `${baseURL}`;

  document.title = `${action} ${entity}`;

  const patchData = async ({ dataToSubmit, signal }) => {
    const resp = await axios.patch(entityURL, dataToSubmit, {
      signal,
      headers: { Authorization: `Bearer ${Auth.retrieveToken()}` },
    });
    return resp;
  };

  const deleteItem = async () => {
    const resp = await axios.delete(entityURL, {
      headers: { Authorization: `Bearer ${Auth.retrieveToken()}` },
    });
    return resp;
  };

  const handleUserActionError = (err, errorString) => {
    if (err?.code === 'ERR_CANCELED') return;

    handleAuthErrors({
      error: err,
      navigate,
      nonAuthRedirectUrl: DASHBOARD_URL,
      redirectUrl: redirectURL,
    });

    setError(errorString);
  };

  const onDeleteAccountClick = async (e) => {
    const idToken = Auth.retrieveIdToken();
    const postAccountDeleteUrl = `${oneloginPostLogouturl}${ACCOUNT_DELETED_URL}`;
    const oneLoginLogoutUrl = `${oneloginUrl}/logout?id_token_hint=${encodeURIComponent(idToken)}&post_logout_redirect_uri=${postAccountDeleteUrl}`;
    e.preventDefault();
    try {
      await deleteItem();
      Auth.logout();
      navigateTo(oneLoginLogoutUrl);
    } catch (err) {
      handleUserActionError(err, 'Sorry, cannot delete your account right now, try again later.');
    }
  };

  const formSubmitHandler = async (e) => {
    e.preventDefault();
    const controller = new AbortController();
    const { signal } = controller;
    setFormDisabled(true);

    if (deletionConfirmed) {
      try {
        if (action === 'Cancel') {
          await patchData({ dataToSubmit: apiHookConfig[0], signal });
        } else {
          await deleteItem();
        }

        navigate(redirectURL, { state: { title, heading } }, { replace: true });
      } catch (err) {
        handleUserActionError(
          err,
          `Cannot ${action.toLowerCase()} this ${entity} right now, try again later`,
        );

        setFormDisabled(false);
      }
    } else {
      navigate(state?.redirectOnCancel || redirectURL, { replace: true });
    }
  };

  return (
    <div className="govuk-grid-row">
      <div className="govuk-grid-column-two-thirds">
        {error && (
          <div
            className="govuk-error-summary"
            aria-labelledby="error-summary-title"
            role="alert"
            tabIndex="-1"
            data-module="govuk-error-summary"
          >
            <h2 className="govuk-error-summary__title">{error}</h2>
          </div>
        )}

        {entity === 'account' ? (
          <>
            {/* Account Deletion Section */}
            <h1 className="govuk-heading-l govuk-!-margin-top-0">
              Are you sure you want to delete your account?
            </h1>
            <p className="govuk-body">
              This will only delete your account for this service and will not delete your GOV.UK
              One Login account. Any voyage reports or data in your account will be kept for up to
              12 months after you delete your account.
            </p>
            <div className="flex-row">
              <button
                onClick={onDeleteAccountClick}
                data-prevent-double-click="true"
                className="govuk-button govuk-button--warning"
                data-module="govuk-button"
              >
                Yes, delete this account
              </button>
              <p className="govuk-body">
                <Link className="govuk-link" to={ACCOUNT_PAGE_URL} data-module="button">
                  No, cancel
                </Link>
              </p>
            </div>
          </>
        ) : (
          <>
            {/* Generic Entity Confirmation Form Section */}
            <form onSubmit={formSubmitHandler}>
              <div className="govuk-form-group">
                <fieldset className="govuk-fieldset">
                  <legend className="govuk-fieldset__legend govuk-fieldset__legend--l">
                    <h1 className="govuk-fieldset__heading">
                      {`Are you sure you want to ${action.toLowerCase()} this ${entity}?`}
                    </h1>
                  </legend>

                  <div className="govuk-hint">This action cannot be undone.</div>

                  <div className="govuk-radios govuk-radios--inline">
                    <div className="govuk-radios__item">
                      <input
                        className="govuk-radios__input"
                        id="confirm-yes"
                        data-testid="confirm-yes"
                        name="confirm_deletion"
                        type="radio"
                        value="yes"
                        onChange={() => setDeletionConfirmed(true)}
                      />
                      <label className="govuk-label govuk-radios__label" htmlFor="confirm-yes">
                        Yes
                      </label>
                    </div>
                    <div className="govuk-radios__item">
                      <input
                        className="govuk-radios__input"
                        id="confirm-no"
                        data-testid="confirm-no"
                        name="confirm_deletion"
                        type="radio"
                        value="no"
                        onChange={() => setDeletionConfirmed(false)}
                      />
                      <label className="govuk-label govuk-radios__label" htmlFor="confirm-no">
                        No
                      </label>
                    </div>
                  </div>
                </fieldset>
              </div>

              <button
                disabled={isFormDisabled}
                type="submit"
                className="govuk-button"
                data-module="govuk-button"
              >
                Continue
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ActionEntity;

ActionEntity.propTypes = {
  notification: PropTypes.shape({
    title: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    entity: PropTypes.string.isRequired,
    baseURL: PropTypes.string.isRequired,
    redirectURL: PropTypes.string.isRequired,
    apiHookConfig: PropTypes.array,
    action: PropTypes.string.isRequired,
  }).isRequired,
};
